import { News } from './types';
import Button from '../Button';
import { BoardCommonControllerService } from '@/__generated__/CommonApi';
import RelDefaultImg from '@/assets/img/img_default.jpg';
import NoImg1 from '@/assets/img/tmp/no_img1@2x.png';
import NoImg2 from '@/assets/img/tmp/no_img2@2x.png';
import { Category, KeysSet } from '@/components/News/WordSet';
import { H2_2 } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { usePopupStore } from '@/stores/PopupStore';
import dayjs from 'dayjs';
import { Link } from 'gatsby';
import React from 'react';
import styled from 'styled-components';

export const SectionContainer = styled.section`
  padding-top: 64px;

  ${breakpoint(`tablet`)} {
    padding-top: 48px;
  }

  &.sec1 {
    padding-top: 96px;
  }

  &.sec2 {
    padding-bottom: 132px;
  }

  .search-container {
    border-bottom: 1px solid #2d2926;
    padding-bottom: 32px;
  }
`;

export const Card = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  .cate {
    background: #1cabe2;
    color: #fff;
    font-size: 16px;
    width: 103px;
    line-height: 40px;
    font-weight: 700;
    text-align: center;
    position: absolute;
    left: 0;
    bottom: -20px;
    z-index: 1;
  }
  .col-img {
    width: 100%;
    position: relative;
    background-image: url(${NoImg1});

    .img-box {
      padding-top: 100%;
    }
  }
  .col-dec {
    width: 100%;
    padding-top: 32px;

    .two-line {
      height: 58px;
      max-height: 58px;

      ${breakpoint(`mobile`)} {
        height: 48px;
        max-height: 48px;
      }
    }

    .dec {
      display: none;
    }

    .tag-wrap {
      min-height: 36px;
      margin-top: 12px;
      ${Button} {
        height: 26px;
        line-height: 24px;
        font-size: 14px;
        padding: 0 5px;
        margin-right: 8px;
        margin-bottom: 8px;
        font-weight: normal;
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  &.big {
    flex-wrap: nowrap;
    align-items: stretch;
    flex-direction: row-reverse;
    background: #f8f9fa;

    .cate {
      font-size: 20px;
      width: 128px;
      line-height: 48px;
      top: 24px;
      bottom: auto;
    }

    .col-img {
      width: 39%;
      position: initial;

      .img-box {
        height: 100%;
      }
    }

    .col-dec {
      width: 61%;
      padding-top: 104px;
      padding-left: 52px;
      padding-right: 108px;

      ${breakpoint(1120)} {
        padding-left: 4.63vw;
        padding-right: 9.64vw;
      }

      .dec {
        display: block;
        margin-top: 24px;
      }

      .tag-wrap {
        margin-top: 16px;
      }

      .date {
        display: none;
      }
    }
  }
`;

export const NewsItemList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -16px;
  margin-top: 32px;
  padding-top: 32px;
  position: relative;

  &::before {
    content: '';
    display: block;
    height: 1px;
    background: #2d2926;
    position: absolute;
    left: 16px;
    right: 16px;
    top: 0;
  }

  & > li {
    padding: 0 16px;
    margin-bottom: 74px;
    width: 33.33%;

    ${breakpoint(`tablet`)} {
      width: 50%;
    }

    ${breakpoint(`mobile`)} {
      width: 100%;
    }

    .link-item {
      display: block;
      width: 100%;
      line-height: initial;
      position: relative;
    }

    &:nth-child(2n + 0) {
      ${Card} {
        .col-img {
          background-image: url(${NoImg2});
        }
      }
    }
  }

  .col-img {
    width: 100%;
    padding-top: 100%;
    height: 0;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      box-sizing: border-box;
      z-index: 1;
      border: 0px solid transparent;
      transition: border 0.2s;
    }

    .img-box {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      transition-duration: 0.6s;
    }

    &:hover {
      .img-box {
        transform: translate(-50%, -50%) scale(1.1);
        transition-duration: 0.8;
      }
      &::before {
        border: 8px solid #1cabe2;
      }
    }
  }
`;

interface NewsItemProps {
  news: News;
  basePath: string;
  queryString: string;
  initialCategory: string;
  onSearch: (value: string) => void;
  handleSearchKey: (value: string) => void;
}

interface SearchFormData {
  category: string;
}
const NewsItem: React.FC<NewsItemProps> = ({
  basePath,
  queryString,
  news,
  initialCategory,
  onSearch,
  handleSearchKey,
}) => {
  let image = news.imgAttGrpNoTn;
  if (image === undefined || image.indexOf(`null`) !== -1) {
    image = RelDefaultImg;
  }

  /* 조회수 증가 */
  const popupStore = usePopupStore();
  const updateViewCnt = async () => {
    const {
      resultCode,
      resultMessage,
    } = await BoardCommonControllerService.viewCntUsingPost({
      boardIndexNumber: news.boardIndexNumber.toString(),
    });

    if (resultCode !== `success`) {
      if (resultMessage !== undefined) {
        popupStore.show(resultMessage);
      } else {
        popupStore.show(
          `소식: ${news.boardIndexNumber} 조회수 증가를 실패했습니다.`,
        );
      }
    }
  };

  return (
    <li key={news.boardIndexNumber}>
      <Card>
        <Link
          to={`${basePath}/${news.boardIndexNumber}${queryString}`}
          className="link-item"
          // onClick={updateViewCnt}
          state={{ boardCategoryCode: news.boardCategoryCode }}
        >
          <div className="col-img">
            <div
              className="img-box"
              css={`
                background-image: url(${image});
              `}
            />
          </div>
          <div className="cate">{Category(`${news.boardCategoryCode}`)}</div>
        </Link>
        <div className="col-dec">
          <Link
            to={`${basePath}/${news.boardIndexNumber}${queryString}`}
            className="link-item"
            // onClick={updateViewCnt}
          >
            <H2_2 className="two-line">{news.subject}</H2_2>
          </Link>

          <div className="tag-wrap">
            {news.relCategoryCode !== null &&
              KeysSet(`${news.relCategoryCode}`).map((key) => (
                <Button
                  type="submit"
                  outline
                  className="tag"
                  onClick={() => {
                    onSearch(key.tag);
                    handleSearchKey(key.tagName);
                  }}
                  name="category"
                >
                  #{key.tagName}
                </Button>
              ))}
          </div>

          <Link
            to={`${basePath}/${news.boardIndexNumber}${queryString}`}
            className="link-item"
            // onClick={updateViewCnt}
          >
            <p className="date">
              {dayjs(news.dispStartDate, `YYYY.MM.DD`).format(`YYYY.MM.DD`)}
            </p>
          </Link>
        </div>
      </Card>
    </li>
  );
};
export default NewsItem;
